import {
  INode,
  IAttribute,
  IValue,
  InteractFn,
} from 'react-interactive-xml-viewer';
import { useStore } from '../../context';
import { Reference, XmlOperator, XmlSelector } from '../../types';

/**
 * Custom hook for creating reference paths in the XML viewer.
 */
export const useXmlPath = () => {
  const setPath = useStore(({ path: p }) => p.setAbsolutePath);

  /**
   * Creates a reference path when the user has clicked on a tag.
   */
  const createTagPath: InteractFn<INode> = (e, node) => {
    const ids = [];
    const references: Omit<Reference, 'next' | 'prev'>[] = [];

    let currentNode: INode | null = node;

    while (currentNode !== null) {
      const parent = currentNode.getParent();

      let operation: number | null = null;

      if (parent) {
        // get childs to find index number of same childs
        const childs = parent
          .getChildren()
          .filter((c) => c.getName() === currentNode!.getName());

        if (childs.length > 1) {
          operation = childs.findIndex(
            (c) => c.getId() === currentNode!.getId()
          );
        }
      }
      // if there are no childs, keep operation at null to not append a number
      references.push({
        type: XmlSelector.Base,
        value: currentNode.getName(),
        operation,
      });

      ids.push(currentNode.getId());
      currentNode = parent;
    }

    setPath(references.reverse(), ids.reverse());
  };

  /**
   * Creates a reference path when the user has clicked on an attribute.
   */
  const createAttributePath: InteractFn<IAttribute> = (e, attr) => {
    // We stop propagation otherwise the tag path will override the attribute path.
    e.stopPropagation();

    const ids = [];
    const references: Omit<Reference, 'next' | 'prev'>[] = [];

    const node = attr.getNode();
    const attrRef = {
      type: XmlOperator.Attribute,
      value: node.getName(),
      operation: `_attrs.${attr.getKey()}`,
    };

    let parent = node.getParent();
    while (parent !== null) {
      references.push({
        type: XmlSelector.Base,
        value: parent.getName(),
        operation: null,
      });
      ids.push(parent.getId());

      parent = parent.getParent();
    }

    setPath(
      [...references.reverse(), attrRef],
      [...ids.reverse(), node.getId()]
    );
  };

  /**
   * Creates a reference path when the user has clicked on a value between tags.
   */
  const createValuePath: InteractFn<IValue> = (e, value) => {
    const ids = [];
    const references: Omit<Reference, 'next' | 'prev'>[] = [];

    const node: INode | null = value.getNode();
    const valueRef = {
      type: XmlOperator.Value,
      value: node.getName(),
      operation: '@',
    };

    let parent = node.getParent();
    while (parent !== null) {
      references.push({
        type: XmlSelector.Base,
        value: parent.getName(),
        operation: null,
      });
      ids.push(parent.getId());

      parent = parent.getParent();
    }

    setPath(
      [...references.reverse(), valueRef],
      [...ids.reverse(), node.getId()]
    );
  };

  return {
    createTagPath,
    createAttributePath,
    createValuePath,
  };
};
